import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  Edit,
  Create,
  Show,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  required,
  FunctionField,
  TabbedShowLayout,
  Tab,
} from 'react-admin';
import { Card, CardContent, Typography, Grid, Chip, Box } from '@mui/material';

const WEI_PER_ATLA = BigInt('1000000000000000000');

const formatTokenAmount = (wei: string) => {
  try {
    const amount = Number(BigInt(wei) / WEI_PER_ATLA);
    return amount.toLocaleString() + ' ATLA';
  } catch {
    return '0 ATLA';
  }
};

const VestingDetails = ({ record }: any) => {
  if (!record?.vestingPlan) return <Typography>No vesting plan</Typography>;

  const { cliffMonths, vestingMonths, startDate, period } = record.vestingPlan;
  const vestingId = record.metadata?.vestingId || 'N/A';
  
  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>📅 Vesting Schedule</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Vesting ID</Typography>
              <Typography variant="body1"><Chip label={vestingId} size="small" color="primary" /></Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Cliff Duration</Typography>
              <Typography variant="body1">{cliffMonths || 0} months</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Vesting Duration</Typography>
              <Typography variant="body1">{vestingMonths || 0} months</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Start Date</Typography>
              <Typography variant="body1">
                {startDate ? new Date(startDate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Period Type</Typography>
              <Typography variant="body1">{period || 'month'}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">Total Duration</Typography>
              <Typography variant="body1">{(cliffMonths || 0) + (vestingMonths || 0)} months</Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AllocationSummary = ({ record }: any) => {
  const total = formatTokenAmount(record?.totalAllocation || '0');
  const claimed = formatTokenAmount(record?.claimedAmount || '0');
  const totalNum = Number(BigInt(record?.totalAllocation || '0') / WEI_PER_ATLA);
  const claimedNum = Number(BigInt(record?.claimedAmount || '0') / WEI_PER_ATLA);
  const remaining = totalNum - claimedNum;
  const percentage = totalNum > 0 ? ((claimedNum / totalNum) * 100).toFixed(2) : '0';

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="caption">Total Allocation</Typography>
            <Typography variant="h5">{total}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
          <CardContent>
            <Typography variant="caption">Claimed</Typography>
            <Typography variant="h5">{claimed}</Typography>
            <Typography variant="caption">{percentage}%</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
          <CardContent>
            <Typography variant="caption">Remaining</Typography>
            <Typography variant="h5">{remaining.toLocaleString()} ATLA</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card sx={{ bgcolor: record?.eligible ? 'success.light' : 'error.light' }}>
          <CardContent>
            <Typography variant="caption">Status</Typography>
            <Typography variant="h5">{record?.eligible ? '✅ Eligible' : '❌ Not Eligible'}</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export const AirdropAllocationList = () => (
  <List>
    <Datagrid rowClick="show">
      <TextField source="address" label="Wallet Address" />
      <FunctionField
        label="Total Allocation"
        render={(record: any) => formatTokenAmount(record.totalAllocation || '0')}
      />
      <FunctionField
        label="Claimed"
        render={(record: any) => formatTokenAmount(record.claimedAmount || '0')}
      />
      <FunctionField
        label="Progress"
        render={(record: any) => {
          const total = Number(BigInt(record.totalAllocation || '0'));
          const claimed = Number(BigInt(record.claimedAmount || '0'));
          const percentage = total > 0 ? ((claimed / total) * 100).toFixed(1) : '0';
          return `${percentage}%`;
        }}
      />
      <FunctionField
        label="Vesting ID"
        render={(record: any) => record.metadata?.vestingId || 'N/A'}
      />
      <BooleanField source="eligible" label="Eligible" />
      <DateField source="createdAt" label="Created At" showTime />
    </Datagrid>
  </List>
);

export const AirdropAllocationShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Summary">
        <FunctionField render={(record: any) => <AllocationSummary record={record} />} />
        <TextField source="address" label="Wallet Address" />
        <BooleanField source="eligible" label="Eligible" />
        <DateField source="createdAt" label="Created At" showTime />
        <DateField source="updatedAt" label="Updated At" showTime />
      </Tab>
      <Tab label="Vesting Details">
        <FunctionField render={(record: any) => <VestingDetails record={record} />} />
      </Tab>
      <Tab label="Raw Data">
        <TextField source="totalAllocation" label="Total Allocation (wei)" />
        <TextField source="claimedAmount" label="Claimed Amount (wei)" />
        <FunctionField
          label="Vesting Plan"
          render={(record: any) => (
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(record.vestingPlan, null, 2)}
            </pre>
          )}
        />
        <FunctionField
          label="Metadata"
          render={(record: any) => (
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(record.metadata, null, 2)}
            </pre>
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export const AirdropAllocationEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput source="address" validate={[required()]} fullWidth disabled />
      <TextInput source="totalAllocation" label="Total Allocation (wei)" validate={[required()]} fullWidth />
      <TextInput source="claimedAmount" label="Claimed Amount (wei)" fullWidth />
      <BooleanInput source="eligible" />
    </SimpleForm>
  </Edit>
);

export const AirdropAllocationCreate = () => (
  <Create transform={(data: any) => {
    // Convert ATLA to wei (1 ATLA = 10^18 wei)
    const totalAllocationInWei = data.totalAllocation ? 
      (BigInt(Math.floor(Number(data.totalAllocation) * 1e18))).toString() : 
      '0';
    
    return {
      ...data,
      totalAllocation: totalAllocationInWei,
      claimedAmount: '0', // Always 0 for new allocations
    };
  }}>
    <SimpleForm>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>📝 Basic Information</Typography>
      <TextInput source="address" validate={[required()]} fullWidth helperText="Ethereum wallet address" />
      <TextInput 
        source="totalAllocation" 
        label="Total Allocation (ATLA)" 
        validate={[required()]} 
        fullWidth 
        helperText="Amount in ATLA tokens (will be converted to wei automatically)"
      />
      <BooleanInput source="eligible" defaultValue={true} label="Eligible for claiming" />

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>📅 Vesting Plan</Typography>
      <NumberInput 
        source="metadata.vestingId" 
        label="Vesting ID" 
        validate={[required()]}
        defaultValue={1}
        min={1}
        fullWidth
        helperText="Vesting plan identifier (1-5)"
      />
    </SimpleForm>
  </Create>
);
